// THE OUTCOME STEP'S PURE RULES — ONE WRITER, READ BY THE SERVER AND BY THE WEBSITE (WEB-PARITY-7).
//
// Moved out of ./record.ts (which re-exports them, so every earlier import still resolves) so the website can call the
// SAME functions through its `@v2-outcome-rules` alias, the way it already calls `@v2-typed-unit` and `@v2-state-code`.
// The website's `/outcome` shows the app's live lines while the owner types the county's final figure — "≈ $X lower than
// your $Y" and "about $Z/yr saved" (LogOutcomeView.swift:176-186) — before anything is saved. Those lines are arithmetic
// on what the owner is typing, so no call can answer them in time; the arithmetic is therefore the server's own, imported,
// over the server's own numbers (the snapshot's assessed value, the summary's rate and ratio). After a save, the server's
// `AppealSummary` is what the screens print. Nothing here has a runtime import, no clock and no I/O.

import type { OutcomeResult } from "../contract/schema";

/** THE RESULTS THE APP CAPTURES A FINAL VALUE FOR — `OutcomeResult.capturesFinalValue`
 *  (AppealAce/Models/Models.swift:81), pinned character for character by
 *  functions/test/v2/outcomes/outcomePins.test.ts. ./record.ts says why the server honours the same rule. */
export const CAPTURES_FINAL_VALUE: readonly OutcomeResult[] = ["won", "partiallyWon", "raised"];

export function capturesFinalValue(result: OutcomeResult): boolean {
  return CAPTURES_FINAL_VALUE.includes(result);
}

/** THE REDUCTION — `LogOutcomeView.reduction` (AppealAce/Features/Outcome/LogOutcomeView.swift:31-34): `assessed − final`
 *  when `assessed > final`, else null. STRICTLY `>`: an assessment left where it was is not a reduction of zero. Mirrored
 *  and pinned character for character by functions/test/v2/outcomes/outcomePins.test.ts. */
export function derivedReduction(assessed: number | null, finalAssessed: number | null): number | null {
  if (assessed === null || finalAssessed === null) return null;
  return assessed > finalAssessed ? assessed - finalAssessed : null;
}

/** HOW FAR A RAISED ASSESSMENT WENT UP — LogOutcomeView.swift:188-192: `result == .raised, let final, let a, final > a`,
 *  then `final - a`. Null unless the final value is above the assessment. Display only: v1's writer records no increase. */
export function raisedBy(assessed: number | null, finalAssessed: number | null): number | null {
  if (assessed === null || finalAssessed === null) return null;
  return finalAssessed > assessed ? finalAssessed - assessed : null;
}

/** THE RESULTS THAT ARE A DECISION — `ReturningOffer.isDecided` (AppealAce/Services/ReturningOffer.swift), whose server
 *  twin `DECIDED_RESULTS` (functions/src/returningOffer.ts) is what counts toward the returning-owner price. A second copy
 *  for the website's thanks line (J1, LogOutcomeView.swift:268, shown only for a decided result the server answered
 *  eligible), pinned to both by functions/test/v2/outcomes/outcomePins.test.ts. */
export const DECIDED_RESULTS_FOR_DISPLAY: readonly OutcomeResult[] = ["won", "partiallyWon", "denied", "raised"];

export function isDecided(result: OutcomeResult): boolean {
  return DECIDED_RESULTS_FOR_DISPLAY.includes(result);
}

/** `LogOutcomeView.annualSavings` (:41-48) → `PropertyTaxRate.annualSaving(assessedReduction:state:ratio:
 *  includeCapStates: true)` (PropertyTaxRate.swift:36-43), with the state's rate already looked up: no cap gate (a logged
 *  win is already real), the ASSESSED reduction converted to MARKET by `÷ ratio` when the ratio is positive, times the
 *  effective rate. Null without a positive reduction or a rate. */
export function annualSavingOf(reduction: number | null, effectiveRate: number | null, ratio: number): number | null {
  if (reduction === null || !(reduction > 0) || effectiveRate === null) return null;
  const marketReduction = ratio > 0 ? reduction / ratio : reduction;
  return marketReduction * effectiveRate;
}

/** The note as the app keeps it — `note.trimmed` (LogOutcomeView.save, :304) — and an empty one is no note. */
export function keptNote(sent: string | undefined): string | null {
  if (sent === undefined) return null;
  const trimmed = sent.trim();
  return trimmed === "" ? null : trimmed;
}
